import ProductDesc from "@/components/Desc/ProductDesc";
import { CountrySelect, CreditLevelSelect, CurrencySelect, DepartmentSelect } from "@/components/Selector";
import PurchaseOrderTypeSelect from "@/pages/Purchase/components/PurchaseOrderTypeSelect";
import SelectLaddersPrice from "@/pages/Purchase/components/SelectLaddersPrice";
import SupplierSelect from "@/pages/Purchase/components/SupplierSelect";
import { PurchaseOrderItemForm } from "@/pages/Purchase/types/purchase";
import SForm from "@/styles/SForm";
import { DictEnum } from "@/types/dict";
import { PurchaseOrderAdd, PurchaseOrderAddItem, PurchaseOrderItem } from "@/types/purchase";
import { DatePicker, Descriptions, DescriptionsProps, FormInstance, FormItemProps, Input, InputNumber, Modal, Select, Typography } from "antd";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Dict from "@/components/Dict";
import SText from "@/styles/SText";
import { Product } from "@/types/product";
import dayjs from "dayjs";
import { divided, multi, noRateAmount } from "@/utils/calc";
import usePage from "@/layout/hooks/usePage";
import AddressSelect from "@/layout/HomeLayout/components/AddressSelect";


const { Title } = Typography;

enum COMPUTED_ENUM {
  NO_CHANGE,
  NO_ROUND,
  DOWN_ROUND,
  UP_ROUND,
  NEAR_ROUND
};

interface PurchaseFormProps {
  disabledItems?: Array<keyof PurchaseOrderItemForm>;
  orderItemId: string;
  data: PurchaseOrderItem | null;
  handleCancel: () => void;
  handleOk: (data: PurchaseOrderAdd, itemData: PurchaseOrderAddItem, itemId: string) => Promise<void>;
  order?: any;
  setRequirementDetail?: (data: any) => void;
  isSupplier?: boolean | undefined;
  btnList?: any;
  canEdit: boolean;
  supplierData?: Array<any>; // 新增：供应商数据，避免重复调用 supplier/list API
}

const PurchaseForm: FC<PurchaseFormProps> = ({
  disabledItems, orderItemId, handleCancel, handleOk, data, order, setRequirementDetail, isSupplier, btnList, canEdit, supplierData
}) => {

  const { t } = useTranslation();
  const { pageId } = usePage();

  const formRef = useRef<FormInstance<PurchaseOrderItemForm>>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [supplierId, setSupplierId] = useState<string>('');
  const [detail, setDetail] = useState<PurchaseOrderItemForm | null>(null);
  const [needComputed, setNeedComputed] = useState<COMPUTED_ENUM | null>(null);
  const [computedTemp, setComputedTemp] = useState<PurchaseOrderItemForm | null>(null);

  useEffect(() => {
    const info = getFormatData(data ?? null);
    setDetail(info)
    if ((info && pageId) || (info && isSupplier)) {
      formRef?.current?.setFieldsValue(info)
      setSupplierId(info.supplierId)
      setProduct(info.itemProduct ?? null)
    }
    return () => {
      setDetail(null)
    }
  }, [data, pageId])

  const getSupplier: (item: PurchaseOrderItemForm) => DescriptionsProps["items"] = (
    item
  ) => [
      {
        key: "code",
        label: t("purchase.code"),
        children: <SText text={item?.supplier?.code || ""} />,
        span: 1,
      },
      {
        key: "shortName",
        label: t("purchase.shortName"),
        children: <SText text={item?.supplier?.shortName || ""} />,
        span: 2,
      },
      {
        key: "name1",
        label: t("purchase.name"),
        children: <SText text={item?.supplier?.name || ""} />,
      },
      {
        key: "country",
        label: t("purchase.country"),
        children: <CountrySelect value={item?.supplier?.country} readOnly />,
      },
      {
        key: "address",
        label: t("purchase.address"),
        children: <SText text={item?.supplier?.address || ""} />,
      },
      {
        key: "zipCode",
        label: t("purchase.zipCode"),
        children: <SText text={item?.supplier?.zipCode || ""} />,
      },
      {
        key: "email",
        label: t("purchase.email"),
        children: <SText text={item?.supplier?.email || ""} />,
      },
      {
        key: "contact",
        label: t("purchase.contact"),
        children: <SText text={item?.supplier?.contact || ""} />,
      },
      {
        key: "phone",
        label: t("purchase.phone"),
        children: <SText text={item?.supplier?.phone || ""} />,
      },
      {
        key: "fax",
        label: t("purchase.fax"),
        children: <SText text={item?.supplier?.fax || ""} />,
      },
      {
        key: "mobile",
        label: t("purchase.mobile"),
        children: <SText text={item?.supplier?.mobile || ""} />,
      },
      {
        key: "account1",
        label: t("purchase.account1"),
        children: <SText text={item?.supplier?.account1 || ""} />,
      },
      {
        key: "bank1",
        label: t("purchase.bank1"),
        children: <SText text={item?.supplier?.bank1 || ""} />,
      },
      {
        key: "swiftCode1",
        label: t("purchase.swiftCode1"),
        children: <SText text={item?.supplier?.swiftCode1 || ""} />,
      },
      {
        key: "account2",
        label: t("purchase.account2"),
        children: <SText text={item?.supplier?.account2 || ""} />,
      },
      {
        key: "bank2",
        label: t("purchase.bank2"),
        children: <SText text={item?.supplier?.bank2 || ""} />,
      },
      {
        key: "swiftCode2",
        label: t("purchase.swiftCode2"),
        children: <SText text={item?.supplier?.swiftCode2 || ""} />,
      },
      {
        key: "currency",
        label: t("purchase.currency"),
        children: <CurrencySelect value={item?.supplier?.currency} readOnly />,
      },
      {
        key: "creditLevel",
        label: t("purchase.creditLevel"),
        children: <CreditLevelSelect value={item?.supplier?.creditLevel} readOnly />,
      },
      {
        key: "creditLine",
        label: t("purchase.creditLine"),
        children: <SText text={item?.supplier?.creditLine || ""} />,
      },
      {
        key: "depositRate",
        label: t("purchase.depositRate"),
        children: <SText text={item?.supplier?.depositRate || ""} suffix="%" />,
      },
      {
        key: "tradeTerm",
        label: t("purchase.tradeTerm"),
        children: (
          <Dict type={DictEnum.PURCHASE_TRADE_TERM} value={item?.supplier?.tradeTerm} readOnly />
        ),
      },
      {
        key: "payMethod",
        label: t("purchase.payMethod"),
        children: (
          <Dict type={DictEnum.PURCHASE_PAY_METHOD} value={item?.supplier?.payMethod} readOnly />
        ),
      },
      {
        key: "taxRate",
        label: t("purchase.taxRate"),
        children: <SText text={item?.itemTaxRate || ""} suffix="%" />,
      },
      {
        key: "org",
        label: t("purchase.org"),
        children: <DepartmentSelect readOnly value={item?.supplier?.orgId} />,
        span: 3,
      },
    ];
  const otherItems: DescriptionsProps["items"] = [
    {
      key: "spareRate",
      label: t('purchase.spareRate'),
      children: data?.spareRate
    },
    {
      key: "spareCount",
      label: t('purchase.spareCount'),
      children: data?.spareCount
    },
    {
      key: "brand",
      label: t('purchase.brand'),
      children: data?.brand
    },
    {
      key: "mpn",
      label: t('purchase.mpn'),
      children: data?.mpn
    },
    {
      key: "dcc",
      label: t('purchase.dcc'),
      children: data?.dcc
    }
  ]
  const formItems: FormItemProps<PurchaseOrderItemForm>[] = [
    {
      label: t("purchase.supplier"),
      name: "supplierId",
      hidden: !!orderItemId,
      children: (
        <SupplierSelect
          disabled={!!orderItemId}
          supplierData={supplierData}
          onChangeCustomer={(e) => {
            setSupplierId(e.id);
            formRef?.current?.setFieldsValue({ supplier: e, itemTaxRate: e.taxRate ?? '0' })
            setProduct(null);
          }}
        />
      ),
      required: true,
      rules: [{ required: true, message: t("purchase.plSupplier") }],
    },
    {
      label: t("purchase.purchaseType"),
      name: "itemType",
      hidden: isSupplier,
      children: <PurchaseOrderTypeSelect readOnly={!!orderItemId} />,
      required: true,
      rules: [{ required: true, message: t("purchase.plPurchaseType") }],
    },
    {
      label: t("purchase.product"),
      name: "itemPriceId",
      hidden: isSupplier || !!orderItemId, // 详情模式下隐藏
      children: (
        <SelectLaddersPrice
          supplierId={supplierId}
          disabled={disabledItems?.includes('itemPrice') || !!isSupplier || !canEdit}
          disabledFetch={!!orderItemId} // 详情模式下禁用数据加载
          onChange={(val, data) => formRef?.current?.setFieldsValue({ itemPrice: data })}
        />
      ),
      required: !orderItemId,
      rules: !orderItemId ? [{ required: true, message: t("purchase.plProduct") }] : undefined,
    },
    {
      noStyle: true,
      hidden: !orderItemId || isSupplier, // 只在详情模式下显示
      children: (
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center' }}>
          <div style={{ color: 'rgba(0, 0, 0, 0.85)', flexShrink: 0 }}>
            <span style={{ color: '#ff4d4f', marginRight: 1 }}>*</span>
            {t("purchase.product")}:
          </div>
          <Input
            disabled={true}
            value={`${detail?.itemProduct?.name || ""}`}
            placeholder={t("purchase.plProduct")}
            style={{ flex: 1 }}
          />
        </div>
      ),
    },
    { name: 'supplier', hidden: true, },
    { name: 'supplierPrice', hidden: true, },
    { name: "itemPrice", hidden: true, },
    { hidden: !product, children: <ProductDesc product={product} showtype={'SUPPLIER'} otherItems={otherItems} /> },
    {
      label: t("purchase.productCount"),
      name: "itemCount",
      children: (
        <InputNumber changeOnWheel disabled={!!isSupplier || !canEdit} placeholder={t("purchase.plProductCount")} precision={0} min={0} style={{ width: '100%' }} />
      ),
    },
    {
      label: t("purchase.taxRate"),
      name: "itemTaxRate",
      hidden: isSupplier,
      children: (
        <InputNumber changeOnWheel disabled={!!isSupplier || !canEdit} placeholder={t("purchase.plTaxRate")} min={0} max={100} style={{ width: '100%' }} addonAfter={canEdit ? '%' : undefined} />
      ),
    },
    {
      label: t("purchase.currency"),
      name: "currency",
      children: (
        <CurrencySelect readOnly={disabledItems?.includes('currency') || !!isSupplier || !canEdit} />
      ),
    },
    {
      name: "payMethod",
      label: t("purchase.payMethod"),
      children: (
        <Dict type={DictEnum.PURCHASE_PAY_METHOD} readOnly={!!isSupplier || !canEdit} />
      ),
    },
    {
      label: t("purchase.scheduledOrderTime"),
      name: "itemScheduledOrderTime",
      hidden: isSupplier,
      children: (
        <DatePicker placeholder={t("purchase.plScheduledOrderTime")} style={{ width: '100%' }} />
      ),
    },
    {
      label: t("purchase.deliveryDeadline"),
      name: "itemDeliveryDeadline",
      hidden: isSupplier,
      children: (
        <DatePicker placeholder={t("purchase.plDeliveryDeadline")} style={{ width: '100%' }} />
      ),
    },
    {
      label: t("purchase.estimateArrivalAt"),
      name: "itemEstimateArrivalAt",
      hidden: isSupplier,
      children: (
        <DatePicker placeholder={t("purchase.plEstimateArrivalAt")} style={{ width: '100%' }} />
      ),
    },
    {
      label: t("purchase.deliveredQuantity"),
      name: "itemDeliveredQuantity",
      hidden: !orderItemId,
      children: (
        <InputNumber changeOnWheel disabled={!!isSupplier || !canEdit} placeholder={!!isSupplier ? '' : t("purchase.plDeliveredQuantity")} precision={0} min={0} style={{ width: '100%' }} />
      ),
    },
    {
      label: t("purchase.productDesc"),
      name: "itemRemark",
      hidden: isSupplier,
      children: (
        <Input.TextArea showCount={canEdit} maxLength={1000} placeholder={t("purchase.plProductDesc")} autoSize={{ minRows: 2, maxRows: 4 }} />
      ),
    },
    {
      label: t("purchase.orderDesc"),
      name: "remark",
      hidden: isSupplier,
      children: (
        <Input.TextArea showCount={canEdit} maxLength={1000} placeholder={t("purchase.plOrderDesc")} autoSize={{ minRows: 2, maxRows: 4 }} />
      ),
    },
    {
      label: t("purchase.amount"),
      name: "amount",
      hidden: !isSupplier,
      children: (
        <Input.TextArea className={'textInput'} showCount={canEdit} maxLength={1000} disabled={true} placeholder={t("purchase.plOrderDesc")} autoSize={{ minRows: 2, maxRows: 4 }} />
      ),
    },
    {
      label: t("purchase.rateAmount"),
      name: "rateAmount",
      hidden: !isSupplier,
      children: (
        <Input.TextArea className={'textInput'} showCount={canEdit} maxLength={1000} disabled={true} placeholder={t("purchase.plOrderDesc")} autoSize={{ minRows: 2, maxRows: 4 }} />
      ),
    },
    {
      label: t("purchase.orderTime"),
      name: "itemOrderTime",
      hidden: !isSupplier,
      children: (
        <DatePicker disabled={isSupplier} placeholder={''} style={{ width: '100%' }} />
      ),
    },
    {
      label: t("purchase.receivingUnit"),
      name: "receiver",
      children: <Input maxLength={120} showCount={canEdit} placeholder={t("purchase.receivingUnit")} />,
    },
    {
      label: t("purchase.receivingAddress"),
      name: "receiverAddress",
      children: (
        // <Dict type={DictEnum.ADDRESS_MANAGE}/>
        <AddressSelect onChange={(_, data) => {
          formRef.current?.setFieldsValue({
            receiverContact: data?.contact,
            receiverMobile: data?.mobile
          })
        }} />
        // <Input.TextArea className={'textInput'} showCount={canEdit} maxLength={1000} placeholder={t("purchase.receivingAddress")} autoSize={{ minRows: 2, maxRows: 4 }} />
        // <Input.TextArea className={'textInput'} showCount={canEdit} maxLength={1000} placeholder={t("purchase.receivingAddress")} autoSize={{ minRows: 2, maxRows: 4 }} />
      ),
    },
    {
      label: t("purchase.contact"),
      name: "receiverContact",
      children: <Input maxLength={120} showCount={canEdit} placeholder={t('purchase.plContact')} />,
    },
    {
      label: t("purchase.phone1"),
      name: "receiverMobile",
      children: <Input maxLength={120} showCount={canEdit} placeholder={t("purchase.phone1")} />,
    },


    {
      noStyle: true,
      hidden: !orderItemId || isSupplier,
      children: <Title level={4}>{t("purchase.supplier")}</Title>
    },
    {
      noStyle: true,
      hidden: !orderItemId || isSupplier,
      children: detail && <Descriptions items={getSupplier(detail)}></Descriptions>
    },
  ]


  const getFormatData: (data: PurchaseOrderItem | null) => PurchaseOrderItemForm | null = data => {
    if (data) {
      return {
        currency: data?.order?.currency || '',
        payMethod: data?.order?.payMethod || '',
        amount: Number(data?.amount),
        rateAmount: Number(data?.rateAmount),
        supplierId: data?.order?.supplierId ?? '',
        supplierPrice: (data?.quotationRequest?.supplierPrice ?? '') + '',
        supplier: data?.order?.supplier ?? null,
        remark: data?.order?.remark,
        receiver: data?.order?.receiver,
        receiverAddress: data?.order?.receiverAddress,
        receiverContact: data?.order?.receiverContact,
        receiverMobile: data?.order?.receiverMobile,
        itemNumber: data.number,
        itemType: data.type,
        itemCount: data.count,
        itemAmount: Number(data?.amount),
        itemRateAmount: Number(data?.rateAmount),
        itemOrderState: data.orderState,
        itemOrderTime: data.orderTime ? dayjs(data.orderTime) : null,
        itemScheduledOrderTime: data.scheduledOrderTime ? dayjs(data.scheduledOrderTime) : null,
        itemDeliveryDeadline: data.deliveryDeadline ? dayjs(data.deliveryDeadline) : null,
        itemDeliveredQuantity: data.deliveredQuantity,
        itemEstimateArrivalAt: data.estimateArrivalAt ? dayjs(data.estimateArrivalAt) : null,
        itemProductId: data?.productId || '',
        itemProduct: data?.product || null,
        itemRemark: data?.remark || '',
        itemLogBookId: data.logBookId,
        itemDocId: data.docId,
        itemPriceId: [data.productId, data.priceId],
        itemTaxRate: data.taxRate,
        itemPrice: {
          expiredAt: data?.price?.standardPrice?.expiredAt ?? null,
          product: data?.price?.product || null,
          productId: data?.price?.product?.id ?? '',
          price: data?.price?.price || '',
          count: data?.price?.count || 0,
          discount: data?.price?.discount || '',
          priceId: data?.price?.id || '',
        }
      }
    } else {
      return null
    }
  }

  const onOk: (data: PurchaseOrderItemForm, roundType: COMPUTED_ENUM) => Promise<void> = (res, roundType) => {
    const rate = divided(res.itemTaxRate ?? '0', '100');
    const defaultCount = res.itemCount ?? 0;
    const price = res.supplierPrice || res.itemPrice.price || ''
    const rateAmountInfo = multi(defaultCount.toString(), price);
    const amountInfo = noRateAmount(rateAmountInfo, rate); // 商品未税总价

    let itemResRateAmount = '0';
    let itemResAmount = '0';
    let orderResRateAmount = '0';
    let orderResAmount = '0';

    if (roundType === COMPUTED_ENUM.NO_CHANGE) {
      itemResRateAmount = data?.rateAmount ?? '0';
      itemResAmount = data?.amount ?? '0';
      orderResRateAmount = data?.order?.rateAmount ?? '0';
      orderResAmount = data?.order?.amount ?? '0';
    } else if (roundType === COMPUTED_ENUM.NO_ROUND) {
      itemResRateAmount = rateAmountInfo;
      itemResAmount = amountInfo;
      orderResRateAmount = rateAmountInfo;
      orderResAmount = amountInfo;
    } else if (roundType === COMPUTED_ENUM.UP_ROUND) {
      itemResRateAmount = Math.ceil(Number(rateAmountInfo ?? '0')) + '';
      itemResAmount = Math.ceil(Number(noRateAmount(itemResRateAmount + '', rate))) + '';
      orderResRateAmount = Math.ceil(Number(rateAmountInfo ?? '0')) + '';
      orderResAmount = Math.ceil(Number(noRateAmount(orderResRateAmount + '', rate))) + '';
    } else if (roundType === COMPUTED_ENUM.DOWN_ROUND) {
      itemResRateAmount = Math.floor(Number(rateAmountInfo ?? '0')) + '';
      itemResAmount = Math.floor(Number(noRateAmount(itemResRateAmount + '', rate))) + '';
      orderResRateAmount = Math.floor(Number(rateAmountInfo ?? '0')) + '';
      orderResAmount = Math.floor(Number(noRateAmount(orderResRateAmount + '', rate))) + '';
    } else if (roundType === COMPUTED_ENUM.NEAR_ROUND) {
      itemResRateAmount = Math.round(Number(rateAmountInfo ?? '0')) + '';
      itemResAmount = Math.round(Number(noRateAmount(itemResRateAmount + '', rate))) + '';
      orderResRateAmount = Math.round(Number(rateAmountInfo ?? '0')) + '';
      orderResAmount = Math.round(Number(noRateAmount(orderResRateAmount + '', rate))) + '';
    } else {//默认还是计算
      itemResRateAmount = rateAmountInfo;
      itemResAmount = amountInfo;
      orderResRateAmount = rateAmountInfo;
      orderResAmount = amountInfo;
    }

    const item: PurchaseOrderAddItem = {
      type: res.itemType,
      sourceId: '',
      priceId: res.itemPriceId[1],
      count: res.itemCount,
      amount: itemResAmount,
      rateAmount: itemResRateAmount,
      remark: res.itemRemark,
      scheduledOrderTime: res.itemScheduledOrderTime ? res.itemScheduledOrderTime.valueOf() : null,
      deliveryDeadline: res.itemDeliveryDeadline ? res.itemDeliveryDeadline.valueOf() : null,
      deliveredQuantity: res.itemDeliveredQuantity ?? 0,
      estimateArrivalAt: res.itemEstimateArrivalAt ? res.itemEstimateArrivalAt.valueOf() : null,
      taxRate: res.itemTaxRate,
    }
    const updateData: PurchaseOrderAdd = {
      amount: orderResAmount,
      rateAmount: orderResRateAmount,
      supplierId: res.supplierId,
      remark: res.remark,
      items: [item],
      currency: res.currency,
      payMethod: res.payMethod,
      receiver: res?.receiver,
      receiverAddress: res?.receiverAddress,
      receiverContact: res?.receiverContact,
      receiverMobile: res?.receiverMobile,
    }
    return handleOk(updateData, item, orderItemId)
  };


  const handleOkValid: (data: PurchaseOrderItemForm) => Promise<boolean | void> = (res) => {
    let resCount = Number(res.itemCount ?? 0);
    let resRate = Number(res.itemTaxRate);
    let dataCount = Number(data?.count ?? 0);
    let dataRate = Number(data?.taxRate ?? 0);

    if (resCount !== dataCount || resRate !== dataRate) {
      setNeedComputed(COMPUTED_ENUM.NO_ROUND);
      setComputedTemp({ ...res });
      return Promise.resolve(true);
    } else return onOk(res, COMPUTED_ENUM.NO_CHANGE);
  };

  const onCancel = () => {
    setSupplierId('')
    setProduct(null)
    setDetail(null)
    formRef?.current?.resetFields();
    handleCancel();
  }

  return (
    <Fragment>
      <SForm<PurchaseOrderItemForm> handleCancel={onCancel}
        handleOk={handleOkValid} data={detail}
        formItems={formItems} modelFormRef={formRef}
        formProps={{ disabled: !canEdit }} order={order}
        setRequirementDetail={setRequirementDetail}
        isSupplier={isSupplier}
        btnList={btnList} />
      {!!computedTemp && needComputed !== null && (
        <Modal title={t("purchase.roundMethod")} width={600} open
          onCancel={() => {
            setComputedTemp(null);
            setNeedComputed(null);
          }}
          onOk={() => onOk(computedTemp, needComputed)}
        >
          <div>{t('purchase.priceComputedTip')}</div>
          <Select<COMPUTED_ENUM>
            value={needComputed}
            options={[
              { label: t("purchase.roundNone"), value: COMPUTED_ENUM.NO_ROUND },
              { label: t("purchase.roundDown"), value: COMPUTED_ENUM.DOWN_ROUND },
              { label: t("purchase.roundUp"), value: COMPUTED_ENUM.UP_ROUND },
              { label: t("purchase.roundNearest"), value: COMPUTED_ENUM.NEAR_ROUND },
            ]}
            onChange={setNeedComputed} style={{ width: "100%" }}
          />
        </Modal>
      )}
    </Fragment>
  )
}

export default PurchaseForm;